import { useEffect, useRef, useState } from "react";
import { ExternalLink, RotateCw } from "lucide-react";

const READY_TIMEOUT_MS = 12000;

// TikTok's official lightweight player (player/v1) with a fallback when it fails to start.
export const TikTokPlayer = ({ short, onEnded }) => {
  const ref = useRef(null);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const endedRef = useRef(onEnded);
  endedRef.current = onEnded;

  useEffect(() => {
    setStatus("loading");
    const timer = setTimeout(() => setStatus((s) => (s === "loading" ? "failed" : s)), READY_TIMEOUT_MS);
    const onMsg = (e) => {
      if (e.source !== ref.current?.contentWindow || !e.data?.["x-tiktok-player"]) return;
      const { type, value } = e.data;
      if (type === "onPlayerReady") setStatus("ready");
      else if (type === "onError") setStatus("error");
      else if (type === "onStateChange" && value === 0) endedRef.current?.();
    };
    window.addEventListener("message", onMsg);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("message", onMsg);
    };
  }, [short.video_id, attempt]);

  // Videos TikTok refuses to play here are skipped automatically after a short pause.
  useEffect(() => {
    if (status !== "error" || !endedRef.current) return;
    const t = setTimeout(() => endedRef.current?.(), 4000);
    return () => clearTimeout(t);
  }, [status]);

  const src =
    `https://www.tiktok.com/player/v1/${short.video_id}` +
    "?autoplay=1&controls=1&progress_bar=1&play_button=1&volume_control=1&fullscreen_button=1" +
    "&music_info=0&description=0&rel=0&native_context_menu=0&closed_caption=0";

  return (
    <>
      <iframe
        key={`${short.video_id}-${attempt}`}
        ref={ref}
        data-testid={`short-player-${short.id}`}
        className="w-full h-full bg-black"
        src={src}
        title={short.title}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen
      />
      {(status === "failed" || status === "error") && (
        <div
          data-testid={`tiktok-fallback-${short.id}`}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 p-6 text-center bg-black/80 backdrop-blur-sm"
        >
          {short.thumbnail_url && (
            <img src={short.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 -z-10" />
          )}
          <p className="text-sm text-zinc-200 max-w-[240px]" data-testid={`tiktok-fallback-msg-${short.id}`}>
            {status === "error"
              ? `TikTok won't play this video here.${endedRef.current ? " Skipping to the next one…" : ""}`
              : "TikTok is taking too long to load this video."}
          </p>
          <button
            data-testid={`tiktok-retry-${short.id}`}
            onClick={() => setAttempt((a) => a + 1)}
            className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-zinc-200 transition-colors"
          >
            <RotateCw className="w-4 h-4" /> Try again
          </button>
          <a
            data-testid={`tiktok-open-${short.id}`}
            href={short.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-white/90 hover:text-white underline-offset-4 hover:underline"
          >
            <ExternalLink className="w-4 h-4" /> Watch on TikTok
          </a>
        </div>
      )}
    </>
  );
};
